import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";
import { Box, Collapse, Drawer, List, Slide } from "@mui/material";
import backburgerIcon from "@iconify/icons-mdi/backburger";
import { InlineIcon } from "@iconify/react";
import {
  OcAppBar,
  OcAppBarContractSelect,
  OcSidebarPartnerLogo,
  OcSidebarSelector,
} from "oc/components";
import { SidebarCollapse, SidebarMenuItem } from "oc/configs/constants";
import {
  DRAWER_WIDTHS,
  MENU_IDS,
  MenuId,
  TRANSITION_DURATION,
} from "oc/configs/menu-constants";
import { settingsSubmenu } from "oc/configs/sidebar-menu";
import { useOcGenerateMainMenu } from "oc/hooks";
import { ocActiveMenuItemState } from "oc/states/active-menu-item-state";
import { ocActiveMenuState } from "oc/states/active-menu-state";
import { ocCustomerLicencesState } from "states";

type OcSidebarProps = {
  narrowMenu: boolean;
  narrowMenuOpened: boolean;
  onNarrowMenuChange: (value: boolean) => void;
  onNarrowMenuOpened: (value: boolean) => void;
  open: boolean;
  showDrawerTransition: boolean;
  variant: "permanent" | "persistent" | "temporary";
  onLogout: () => void;
  isMobile: boolean;
};

export const OcSidebar = ({
  narrowMenu,
  narrowMenuOpened,
  onNarrowMenuChange,
  onNarrowMenuOpened,
  open,
  showDrawerTransition,
  variant,
  onLogout,
  isMobile,
}: OcSidebarProps) => {
  const [drawerWidth, setDrawerWidth] = useState<number>(
    narrowMenu ? DRAWER_WIDTHS.NARROW : DRAWER_WIDTHS.EXPANDED,
  );
  const licences = useRecoilValue(ocCustomerLicencesState);

  const [menu, setMenu] = useState<SidebarMenuItem[] | SidebarCollapse[]>([]);
  const mainMenu = useOcGenerateMainMenu();
  const [activeMenuItem, setActiveMenuItem] = useRecoilState(
    ocActiveMenuItemState,
  );

  const [activeMenu, setActiveMenu] = useRecoilState(ocActiveMenuState);

  const transitionStyles = {
    entering: { width: isMobile ? 0 : DRAWER_WIDTHS.NARROW },
    entered: { width: isMobile ? 0 : DRAWER_WIDTHS.NARROW },
    exiting: { width: isMobile ? window.outerWidth : DRAWER_WIDTHS.EXPANDED },
    exited: { width: isMobile ? window.outerWidth : DRAWER_WIDTHS.EXPANDED },
  };

  useEffect(() => {
    if (activeMenu === MENU_IDS.SETTINGS) {
      if (!_.isEqual(settingsSubmenu, menu)) {
        setMenu(settingsSubmenu);
      }
    } else if (activeMenu === MENU_IDS.MAIN) {
      if (!_.isEqual(mainMenu, menu)) {
        setMenu(mainMenu as SidebarCollapse[]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, mainMenu]);

  useEffect(() => {
    if (narrowMenuOpened) {
      setDrawerWidth(transitionStyles.exited.width);
    } else {
      setDrawerWidth(
        narrowMenu
          ? transitionStyles.entered.width
          : transitionStyles.exited.width,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrowMenu, narrowMenuOpened, isMobile]);

  const handleMenuItemChange = (id: string) => {
    if (id.includes("collapse") || id?.length === 0) {
      return;
    }
    setActiveMenuItem(id);
  };

  const handleMenuChange = (id: string) => {
    setActiveMenu(id as MenuId);
  };

  return (
    <Box>
      <Box>
        <Drawer
          variant={variant}
          anchor="left"
          open={open}
          id="leftDrawer"
          PaperProps={{
            sx: {
              backgroundColor: "#212121",
              width: drawerWidth,
              transition: showDrawerTransition
                ? `width ${TRANSITION_DURATION}ms ease-in-out`
                : "none",
              zIndex: 1300,
              marginTop: isMobile ? "72px" : 0,
              borderRight: "unset",
              overflowX: "hidden",
            },
          }}
          onMouseEnter={() => {
            if (narrowMenu) {
              onNarrowMenuOpened(true);
            }
          }}
          onMouseLeave={() => {
            if (narrowMenu) {
              onNarrowMenuOpened(false);
            }
          }}
        >
          <Box
            id="oc-sidebar-menu"
            sx={{
              background: "#212121",
              overflow: "hidden",
              maxHeight: isMobile ? "calc(100vh - 64px)" : "unset",
            }}
          >
            {isMobile && (
              <>
                <Box style={{ backgroundColor: "#fff" }}>
                  <OcAppBarContractSelect isMobile={isMobile} />
                </Box>
                <OcAppBar onLogout={onLogout} />
              </>
            )}
            {!isMobile && (
              <Box
                id="oc-sidebar-menu-top"
                height="64px"
                sx={{
                  backgroundColor: "#CC0033",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                }}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Collapse in={!narrowMenu || narrowMenuOpened}>
                  <OcSidebarPartnerLogo />
                </Collapse>
                <Collapse in={!narrowMenu && !narrowMenuOpened}>
                  <Box
                    id="oc-sidebar-narrow-menu-icon"
                    height="64px"
                    width="52px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => onNarrowMenuChange(!narrowMenu)}
                    sx={{ transform: narrowMenu ? "rotate(180deg)" : "none" }}
                  >
                    <InlineIcon icon={backburgerIcon} height="26px" />
                  </Box>
                </Collapse>
                <Collapse in={narrowMenu && !narrowMenuOpened}>
                  <Box
                    height="64px"
                    width="52px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => onNarrowMenuChange(!narrowMenu)}
                  >
                    <img
                      src="/img/owIconLogoTransparent.png"
                      alt="ow-logo"
                      width="32"
                    ></img>
                  </Box>
                </Collapse>
                <Collapse in={narrowMenuOpened}>
                  <Box
                    id="oc-sidebar-narrow-menu-icon"
                    height="64px"
                    width="52px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => onNarrowMenuChange(!narrowMenu)}
                    style={
                      narrowMenu ? { transform: "rotate(180deg)" } : undefined
                    }
                  >
                    <InlineIcon icon={backburgerIcon} height="26px" />
                  </Box>
                </Collapse>
              </Box>
            )}
            {activeMenu === MENU_IDS.MAIN && (
              <Slide in={activeMenu === MENU_IDS.MAIN} direction="right">
                <List style={{ padding: "0px" }}>
                  {menu.map((item, index) => {
                    if (!item?.licence || !licences[item.licence]) {
                      return null;
                    }
                    return (
                      <OcSidebarSelector
                        key={`${index}_${item.id}`}
                        item={item}
                        onMenuChange={handleMenuChange}
                        onMenuItemChange={handleMenuItemChange}
                        activeMenuItem={activeMenuItem}
                        narrowMenu={narrowMenu}
                        narrowMenuOpened={narrowMenuOpened}
                      />
                    );
                  })}
                </List>
              </Slide>
            )}
            {activeMenu !== MENU_IDS.MAIN && (
              <Slide in direction="left">
                <List>
                  {menu.map((item, index) => {
                    if (!item?.licence || !licences[item.licence]) {
                      return null;
                    }

                    return (
                      <OcSidebarSelector
                        key={`${index}_${item.id}`}
                        item={item}
                        onMenuChange={handleMenuChange}
                        onMenuItemChange={handleMenuItemChange}
                        activeMenuItem={activeMenuItem}
                        narrowMenu={narrowMenu}
                        narrowMenuOpened={narrowMenuOpened}
                      />
                    );
                  })}
                </List>
              </Slide>
            )}
          </Box>
        </Drawer>
      </Box>
    </Box>
  );
};

export default OcSidebar;
